import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
import type { SurgeOverride } from "@tdelivr/api-client/generated/pricing/dynamic-pricing.js";

import {
  badgeFor,
  EXPORT_HEADER,
  exportRow,
  expiryProblem,
  MAXIMUM_OVERRIDE_MINUTES,
  MULTIPLIER_OPTIONS,
  ORDER_CHOICES,
  PER_PAGE_CHOICES,
  STANDING_FILTERS,
  supplyCell,
  surgeCell,
  surgePage,
  tileRow,
  tilesFor,
  zoneDetail,
  zoneRows,
  type SurgeOrder,
  type SurgeStanding,
  type SurgeZoneRow,
  type Zone,
  type ZoneSupplyRow,
} from "../surge.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { RecordDetail } from "./RecordDetail.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * Surge and zones: live demand and supply by zone, and surge in effect.
 *
 * Built to `19 WP18 Dispatch Operations.dc.html`, page `surge`.
 *
 * **This was the last unbuilt screen on the platform.** `docs/design/UNBUILT_SCREENS.md` recorded
 * it blocked five times, and each reading found the blocker one level deeper than the last. The
 * override half closed on 22 October when PRC-03 gained a zone-scoped manual override. The middle
 * column closed when DSP-06 was built for it — a projection that counts demand and supply per zone
 * from events, rather than a composition that pays one containment call per driver per refresh.
 *
 * **Three services, and the design cites one of them.** The design says `PRC-03 · GEO-02`: GEO-02
 * is the reverse-geocoding adapter, which turns a point into an address and has nothing to do with
 * a zone. The heading names what this page actually reads, which is the rule this console keeps.
 *
 * **One tile is drawn as an absence, and that is the honest build.** `Surge zones` wants every zone
 * where a multiplier above 1.0 is in force. Automatic surge is not stored anywhere on this
 * platform — PRC-03 computes an adjustment per quote and keeps none of it — so the manual
 * overrides are a floor on that number rather than the number. Q-246.
 */
export interface SurgeZonesProps {
  readonly transport: Transport;
  readonly tenantId: string;
  /** Innermost last: tenant, section, page. */
  readonly crumbs?: readonly string[];
  /** What this operator holds, which decides whether the form sends or explains. */
  readonly scopes?: ReadonlySet<string>;
  /** Injected by the tests, so a row's expiry and the clock cannot disagree in a snapshot. */
  readonly now?: () => number;
}

const WRITE_SCOPE = "dynamic-pricing:write";
const pricing = services.pricing__dynamic_pricing;
const zoneSupply = services.dispatch__zone_supply_read_model;
const areasApi = services.geo__service_area;

/** GEO-06 pages its areas; one page is every zone a dispatch board draws. */
const PER_FETCH = "200";

/** The export formats the design's menu offers, in its order and its words. */
const EXPORTS = [
  { format: "csv", glyph: "description", label: "CSV" },
  { format: "xls", glyph: "table_view", label: "Excel (.xls)" },
  { format: "pdf", glyph: "picture_as_pdf", label: "PDF" },
] as const;

type ExportFormat = (typeof EXPORTS)[number]["format"];

interface Loaded {
  readonly areas: readonly Zone[];
  readonly counts: readonly ZoneSupplyRow[];
  readonly overrides: readonly SurgeOverride[];
}

export function SurgeZones({
  transport,
  tenantId,
  crumbs,
  scopes,
  now,
}: SurgeZonesProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  const [formOpen, setFormOpen] = useState(false);
  const [zoneId, setZoneId] = useState("");
  const [basisPoints, setBasisPoints] = useState(MULTIPLIER_OPTIONS[1]?.basisPoints ?? 12000);
  const [expiresAt, setExpiresAt] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [formFailure, setFormFailure] = useState<string | undefined>(undefined);
  const [ending, setEnding] = useState<string | undefined>(undefined);

  const [search, setSearch] = useState("");
  const [standing, setStanding] = useState<SurgeStanding | "override" | undefined>(undefined);
  const [order, setOrder] = useState<SurgeOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(PER_PAGE_CHOICES[0] ?? 10);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRegion = useRef<HTMLSpanElement>(null);

  const load = useCallback(async () => {
    const areaClient = areasApi.createServiceAreaClient(transport);
    const supplyClient = zoneSupply.createZoneSupplyReadModelClient(transport);
    const pricingClient = pricing.createDynamicPricingClient(transport);
    try {
      /*
       * Together, because the board is one reading. Three sequential requests would draw a zone's
       * counts against an override list taken a moment later, and the one row where that matters
       * is the row somebody is acting on.
       */
      const [areas, supply, overrides] = await Promise.all([
        areaClient.getServiceAreas("active", PER_FETCH, undefined),
        supplyClient.readZoneSupply(),
        pricingClient.listSurgeOverrides(PER_FETCH, undefined),
      ]);
      setLoaded({ areas: areas.areas, counts: supply.zones, overrides: overrides.overrides });
      setFailure(undefined);
    } catch (error) {
      setFailure(
        stateForError(error, [
          "service-area:read",
          "dispatch-operations:read",
          "dynamic-pricing:read",
        ]),
      );
      setLoaded(undefined);
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  /* Dismiss the export menu on Escape or a click outside it, as every other grid here does. */
  useEffect(() => {
    if (!exportOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setExportOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const region = exportRegion.current;
      if (region !== null && !region.contains(event.target as Node)) setExportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [exportOpen]);

  const instant = now === undefined ? Date.now() : now();
  const rows = useMemo(
    () =>
      loaded === undefined
        ? undefined
        : zoneRows(loaded.areas, loaded.counts, loaded.overrides, instant),
    [loaded, instant],
  );
  /*
   * The tiles count every zone, never the filtered page. A `Balanced` tile that moved when
   * somebody typed in the search box would be a different claim about the estate on every
   * keystroke, which is the opposite of what a tile is for.
   */
  const tiles = useMemo(() => tilesFor(rows ?? []), [rows]);
  const view = useMemo(
    () =>
      surgePage(rows ?? [], {
        search,
        ...(standing === undefined ? {} : { standing }),
        order,
        page,
        perPage,
      }),
    [rows, search, standing, order, page, perPage],
  );
  const selected = useMemo(
    () => (rows ?? []).find((row) => row.zoneId === selectedId),
    [rows, selectedId],
  );

  /*
   * `undefined` scopes is a caller that does not know, drawn as able to act. Hiding a control from
   * somebody who holds the scope is how a permission question becomes a bug report about a missing
   * feature.
   */
  const viewOnly = scopes !== undefined && !scopes.has(WRITE_SCOPE);
  const expiryFault = expiresAt === "" ? undefined : expiryProblem(expiresAt, instant);
  const sendable =
    zoneId !== "" && reason.trim().length > 0 && expiresAt !== "" && expiryFault === undefined;

  const clearFilters = (): void => {
    setSearch("");
    setStanding(undefined);
    setPage(1);
  };

  const apply = (): void => {
    if (!sendable) return;
    setBusy(true);
    setFormFailure(undefined);
    const client = pricing.createDynamicPricingClient(transport);
    client
      .raiseSurgeOverride(
        {
          zoneId,
          multiplierBasisPoints: basisPoints,
          reason: reason.trim(),
          expiresAt: new Date(expiresAt).toISOString(),
        },
        {
          /*
           * Derived from the zone and the expiry the operator chose, so pressing Apply twice
           * replays rather than raising two overrides on one zone — which PRC-03 would refuse
           * under its advisory lock, but as a 409 the operator then has to interpret.
           */
          idempotencyKey: `surge-override/${tenantId}/${zoneId}/${expiresAt}`.slice(0, 200),
        },
      )
      .then(() => {
        setFormOpen(false);
        setZoneId("");
        setReason("");
        setExpiresAt("");
        return load();
      })
      .catch((error: unknown) => {
        setFormFailure(error instanceof Error ? error.message : "PRC-03 refused the override.");
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const end = (overrideId: string): void => {
    setEnding(overrideId);
    setFormFailure(undefined);
    const client = pricing.createDynamicPricingClient(transport);
    client
      .endSurgeOverride(overrideId)
      .then(() => load())
      .catch((error: unknown) => {
        setFormFailure(
          error instanceof Error ? error.message : "PRC-03 refused to end the override.",
        );
      })
      .finally(() => {
        setEnding(undefined);
      });
  };

  /*
   * Handed to `StateView` with the grid as its children rather than branched on here, which is how
   * every other board on this console is built. The early-return form looks equivalent and is not:
   * `stateForList` answers a state for the ready case too, so `state !== undefined` is always true
   * and the table never renders. `Broadcasts` had exactly that bug until this page was written.
   */
  const state = failure ?? stateForList(rows, {});

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="surge__head">
      <span>Zone</span>
      <span>Demand / supply</span>
      <span>Surge</span>
      <span className="surge__head--right">State</span>
    </div>
  );

  return (
    <div className="board-split">
      <section className="page surge">
        <PageHeading
          crumbs={crumbs ?? []}
          /*
           * The design says `PRC-03 · GEO-02`. GEO-02 is the reverse-geocoding adapter, which turns
           * a point into an address; the Zone column needs GEO-06, which owns the areas. And the
           * middle column is DSP-06, which did not exist when the design was cut.
           */
          source="DSP-06 · PRC-03 · GEO-06"
          title="Surge and zones"
          description="Live demand and supply by zone, and surge in effect."
          action={
            <button
              type="button"
              className="surge__primary"
              disabled={viewOnly}
              title={
                viewOnly
                  ? `Needs ${WRITE_SCOPE}. Your role can view only.`
                  : "Raises a multiplier on one zone until a time you choose."
              }
              onClick={() => setFormOpen(true)}
            >
              <span aria-hidden="true" className="surge__primary-glyph">
                bolt
              </span>
              Override surge
            </button>
          }
        />

        <div className="surge__tiles">
          {tileRow(tiles).map((tile) => (
            <div key={tile.label} className={`surge__tile surge__tile--${tile.classKey}`}>
              <span className="surge__tile-label">{tile.label}</span>
              <span className="surge__tile-value">
                {tile.value === undefined ? (
                  <span className="surge__tile-absent" title={tile.title}>
                    —
                  </span>
                ) : (
                  tile.value
                )}
              </span>
              {/*
                The design's third line is a badge, not a caption: `badge(class, note || "—")`.
                Drawn through `StateBadge` so a tile and a row of the same class cannot end up
                with different glyphs.
              */}
              <StateBadge
                classKey={tile.classKey}
                label={tile.note}
                code={tile.code}
                status={tile.status}
                {...(tile.title === undefined ? {} : { title: tile.title })}
              />
            </div>
          ))}
        </div>

        {/*
          The design draws this as a modal over the board, not as a panel above it.

          `formUI` centres a `min(680px,100%)` panel on an `rgba(12,17,29,.55)` overlay at
          z-index 200, with a three-row grid — head, scrolling body, footer on `--sunken` — and
          docks it to the bottom of the screen below 640. Every measurement here is that function's,
          and the class names follow `Geofences`, which built the same dialog from the same source.
        */}
        {formOpen ? (
          <div
            className="surge__overlay"
            role="presentation"
            onClick={() => {
              setFormOpen(false);
              setFormFailure(undefined);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Escape") return;
              setFormOpen(false);
              setFormFailure(undefined);
            }}
          >
            <form
              className="surge__dialog"
              role="dialog"
              aria-modal="true"
              aria-label="Override surge in a zone"
              /* The overlay closes on a click; a click inside the panel is not one. */
              onClick={(event) => event.stopPropagation()}
              onSubmit={(event) => {
                event.preventDefault();
                apply();
              }}
            >
              <div className="surge__dialog-head">
                <span aria-hidden="true" className="surge__dialog-glyph">
                  bolt
                </span>
                <span className="surge__dialog-title">
                  <strong>Override surge in a zone</strong>
                  <span className="surge__dialog-sub">
                    Overrides expire on their own and need a reason.
                  </span>
                </span>
                <button
                  type="button"
                  className="surge__dialog-close"
                  aria-label="Close"
                  disabled={busy}
                  onClick={() => {
                    setFormOpen(false);
                    setFormFailure(undefined);
                  }}
                >
                  <span aria-hidden="true">close</span>
                </button>
              </div>

              <div className="surge__dialog-body">
                {formFailure === undefined ? null : (
                  <div className="surge__dialog-error" role="alert">
                    <span aria-hidden="true" className="surge__dialog-error-glyph">
                      error
                    </span>
                    <span>{formFailure}</span>
                  </div>
                )}

                <fieldset className="surge__fieldset">
                  {/* The design's own section heading for this form. */}
                  <legend>Override</legend>
                  <div className="surge__field-grid">
                    <label className="surge__field" htmlFor="surge-zone">
                      <span className="surge__label">
                        Zone
                        <span className="surge__required" aria-hidden="true">
                          *
                        </span>
                      </span>
                      <select
                        id="surge-zone"
                        value={zoneId}
                        disabled={busy}
                        onChange={(event) => setZoneId(event.target.value)}
                      >
                        <option value="">Choose a zone</option>
                        {(loaded?.areas ?? []).map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.name}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="surge__field" htmlFor="surge-multiplier">
                      <span className="surge__label">
                        Multiplier
                        <span className="surge__required" aria-hidden="true">
                          *
                        </span>
                      </span>
                      <select
                        id="surge-multiplier"
                        value={String(basisPoints)}
                        disabled={busy}
                        onChange={(event) => setBasisPoints(Number(event.target.value))}
                      >
                        {MULTIPLIER_OPTIONS.map((option) => (
                          <option key={option.basisPoints} value={String(option.basisPoints)}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="surge__field" htmlFor="surge-expires">
                      <span className="surge__label">
                        Expires
                        <span className="surge__required" aria-hidden="true">
                          *
                        </span>
                      </span>
                      <input
                        id="surge-expires"
                        type="datetime-local"
                        value={expiresAt}
                        disabled={busy}
                        aria-invalid={expiryFault === undefined ? undefined : true}
                        onChange={(event) => setExpiresAt(event.target.value)}
                      />
                      {/*
                        The design's `Max 2 hours`, replaced by the fault where there is one rather
                        than joined by it, so the field never grows a row under a reader. PRC-03
                        refuses it either way — the bound is a named constraint on its table — and
                        this is the sentence instead of a 400 somebody has to interpret.
                      */}
                      <span
                        className={
                          expiryFault === undefined ? "surge__hint" : "surge__hint surge__hint--bad"
                        }
                      >
                        {expiryFault ?? `Max ${String(MAXIMUM_OVERRIDE_MINUTES / 60)} hours`}
                      </span>
                    </label>

                    {/* The design puts an area across both columns. */}
                    <label className="surge__field surge__field--wide" htmlFor="surge-reason">
                      <span className="surge__label">
                        Reason
                        <span className="surge__required" aria-hidden="true">
                          *
                        </span>
                      </span>
                      <textarea
                        id="surge-reason"
                        rows={3}
                        value={reason}
                        disabled={busy}
                        onChange={(event) => setReason(event.target.value)}
                      />
                    </label>
                  </div>
                </fieldset>

                <div className="surge__dialog-note">
                  <span aria-hidden="true" className="surge__dialog-note-glyph">
                    lock
                  </span>
                  <span>A second person can end any override early.</span>
                </div>
              </div>

              <div className="surge__dialog-foot">
                <button
                  type="button"
                  className="surge__dialog-cancel"
                  disabled={busy}
                  onClick={() => {
                    setFormOpen(false);
                    setFormFailure(undefined);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="surge__dialog-save"
                  disabled={busy || !sendable}
                  title={
                    sendable
                      ? "Raises the multiplier on this zone until the time you chose."
                      : "PRC-03 requires a zone, a multiplier, an expiry inside two hours and a reason. The reason is what the next person reads when they decide whether to end it."
                  }
                >
                  {busy ? "Applying…" : "Apply override"}
                </button>
              </div>
            </form>
          </div>
        ) : null}

        {/*
          One card, as every grid on this console is: the toolbar, the rows and the pager share a
          16px-radius surface with a hairline between each band. The toolbar was briefly outside it
          and the pager inside, which drew a floating bar above a card — the chrome belongs to the
          panel, not to the rows.
        */}
        <div className="surge__panel">
          {/*
          Design rule 4's toolbar, working over what the board holds.

          Every control here is client-side, and that is a property of the three reads rather than a
          shortcut: DSP-06 answers every zone in one read with no query at all, PRC-03's listing
          takes only a cursor, and GEO-06's takes a lifecycle. There is no server-side search,
          filter or sort to delegate to — and the set is bounded by how many zones a tenant
          operates, which is tens, so this is the whole estate rather than a window onto it.
        */}
          <div className="surge__toolbar">
            <span className="surge__search">
              <span aria-hidden="true" className="surge__search-glyph">
                search
              </span>
              <input
                id="surge-search"
                type="search"
                aria-label="Search"
                placeholder="Search"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(1);
                }}
              />
            </span>
            <select
              id="surge-standing"
              aria-label="Filter by state"
              value={standing ?? ""}
              onChange={(event) => {
                setStanding(
                  event.target.value === ""
                    ? undefined
                    : (event.target.value as SurgeStanding | "override"),
                );
                setPage(1);
              }}
            >
              <option value="">All states</option>
              {STANDING_FILTERS.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            <select
              id="surge-order"
              aria-label="Sort"
              value={order}
              onChange={(event) => {
                setOrder(event.target.value as SurgeOrder);
              }}
            >
              {ORDER_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
            <span className="surge__export" ref={exportRegion}>
              <button
                type="button"
                className="surge__export-button"
                aria-expanded={exportOpen}
                onClick={() => {
                  setExportOpen((open) => !open);
                }}
              >
                <span aria-hidden="true" className="surge__export-glyph">
                  download
                </span>
                Export
              </button>
              {exportOpen ? (
                <div className="surge__export-menu" role="menu">
                  {EXPORTS.map((choice) => (
                    <button
                      key={choice.format}
                      type="button"
                      role="menuitem"
                      className="surge__export-item"
                      onClick={() => {
                        setExportOpen(false);
                        /* Every matching row, not the page on screen. */
                        exportRows(view.all, choice.format);
                      }}
                    >
                      <span aria-hidden="true" className="surge__export-item-glyph">
                        {choice.glyph}
                      </span>
                      {choice.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </span>
          </div>

          <StateView
            state={state}
            purpose="Every service area appears here with the demand and supply counted in it."
            onRetry={() => {
              void load();
            }}
            skeleton={{
              head,
              columns: 4,
              rowClassName: "surge__row",
              // This board wraps its grid, and the wrapper is what puts it on a surface.
              wrapperClassName: "surge__grid",
              // Both strings come from the trail the shell already draws, so the
              // sentence can never disagree with the breadcrumb above it.
              page: crumbs?.at(-1) ?? "this page",
              tenant: crumbs?.[0] ?? "this tenant",
            }}
          >
            <div className="surge__grid">
              {head}

              {view.rows.map((row) => {
                const badge = badgeFor(row);
                const supply = supplyCell(row);
                const surge = surgeCell(row);
                return (
                  <div
                    key={row.zoneId}
                    data-selected={row.zoneId === selectedId ? "true" : undefined}
                    role="button"
                    tabIndex={0}
                    aria-pressed={row.zoneId === selectedId}
                    aria-label={`Open zone ${row.name}`}
                    onClick={() => setSelectedId(row.zoneId)}
                    onKeyDown={(event) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      setSelectedId(row.zoneId);
                    }}
                    /*
                     * Two of the design's nine classes tint a row, and this board produces both:
                     * violet for a zone a person has overridden, red for one with demand and no
                     * supply. The tint is how those rows are found without reading the badge.
                     */
                    className={
                      badge.classKey === "human" || badge.classKey === "failed"
                        ? `surge__row surge__row--${badge.classKey}`
                        : "surge__row"
                    }
                  >
                    <span className="surge__zone">{row.name}</span>
                    <span className="surge__counts" title={supply.title}>
                      {supply.text}
                    </span>
                    <span
                      className={
                        surge.absent ? "surge__multiplier surge__absent" : "surge__multiplier"
                      }
                      title={surge.title}
                    >
                      {surge.text}
                    </span>
                    <span className="surge__state">
                      <StateBadge
                        classKey={badge.classKey}
                        label={badge.label}
                        code={badge.code}
                        status={badge.status}
                        {...(badge.status === "stretched"
                          ? {
                              title:
                                "More demand than supply, which is where surge is asked for. The word describes the zone, not a multiplier read from PRC-03 — the Surge column carries that.",
                            }
                          : {})}
                      />
                    </span>
                  </div>
                );
              })}
            </div>

            {view.total === 0 ? (
              <div className="surge__no-match">
                <span aria-hidden="true" className="surge__no-match-glyph">
                  search_off
                </span>
                Nothing matches this search or filter.
                <button type="button" className="surge__clear" onClick={clearFilters}>
                  Clear filters
                </button>
              </div>
            ) : null}

            <div className="surge__pager">
              <span>
                {view.total === 0
                  ? "No matching zones"
                  : `Showing ${String((page - 1) * perPage + 1)}–${String(Math.min(page * perPage, view.total))} of ${String(view.total)}`}
              </span>
              <span className="surge__pager-controls">
                <select
                  id="surge-per-page"
                  aria-label="Rows per page"
                  value={perPage}
                  onChange={(event) => {
                    setPerPage(Number(event.target.value));
                    setPage(1);
                  }}
                >
                  {PER_PAGE_CHOICES.map((choice) => (
                    <option key={choice} value={choice}>
                      {choice} per page
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  aria-label="Previous page"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  <span aria-hidden="true">chevron_left</span>
                </button>
                <span className="surge__page">
                  Page {Math.min(page, view.pageCount)} of {view.pageCount}
                </span>
                <button
                  type="button"
                  aria-label="Next page"
                  disabled={page >= view.pageCount}
                  onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
                >
                  <span aria-hidden="true">chevron_right</span>
                </button>
              </span>
            </div>
          </StateView>
        </div>

        <p className="surge__note">
          <span aria-hidden="true" className="surge__note-glyph">
            lock
          </span>
          Manual surge overrides need a reason and expire on their own.
        </p>
      </section>

      {selected === undefined ? null : (
        <RecordDetail
          record={zoneDetail(selected)}
          open
          onClose={() => setSelectedId(undefined)}
          {...(selected.override === undefined || viewOnly
            ? {}
            : {
                action: (
                  <button
                    type="button"
                    className="surge__end"
                    disabled={ending !== undefined}
                    title="Ends it now rather than at its expiry. Who ended it is recorded."
                    onClick={() => {
                      end(endableId(selected));
                    }}
                  >
                    {ending === undefined ? "End override" : "Ending…"}
                  </button>
                ),
              })}
        />
      )}
    </div>
  );
}

/**
 * Write the matching zones out, in the three formats the design's menu offers.
 *
 * The same shape `ActiveRides` and the demand queue use: a real CSV, an Excel-readable HTML table,
 * and the browser's own print dialog for the PDF. No library, because a dependency that renders a
 * four-column table is a dependency whose version has to be minded forever.
 */
function exportRows(rows: readonly SurgeZoneRow[], format: ExportFormat): void {
  const header = [...EXPORT_HEADER];
  const body = rows.map((row) => [...exportRow(row)]);

  if (format === "csv") {
    const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...body].map((line) => line.map(quote).join(",")).join("\n");
    download(new Blob([csv], { type: "text/csv" }), "surge-and-zones.csv");
    return;
  }

  const table =
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>` +
    body
      .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("") +
    "</table>";

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      "surge-and-zones.xls",
    );
    return;
  }

  const printable = window.open("", "_blank");
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>surge and zones</title></head><body><h2 style="font-family:sans-serif">Surge and zones</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}

/**
 * The override to end, narrowed for the caller.
 *
 * The button is drawn only where `override` is defined, and this keeps that narrowing out of the
 * JSX rather than asserting it there.
 */
function endableId(row: SurgeZoneRow): string {
  return row.override?.overrideId ?? "";
}
